import { computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

export default function useDayjs() {
  const instance = getCurrentInstance();
  if (!instance) {
    throw new Error('useDayjs must be called within the setup() function.');
  }

  const { locale } = useI18n();
  const { $dayjs } = instance.proxy.$root;

  // Day.js uses 'zh-cn' while vue-i18n uses 'zh'
  const dayjsLocale = computed(() => (locale.value === 'zh' ? 'zh-cn' : locale.value));

  // Format ATOM
  const ATOM_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
  /**
   * Parse date
   * @param date
   * @returns {*}
   */
  function parseDate(date) {
    let dateDayJs = null;

    if (Object.prototype.toString.call(date) === '[object String]') {
      dateDayJs = $dayjs(date);
    } else if (Number.isInteger(date)) {
      dateDayJs = $dayjs.unix(date);
    } else if (date instanceof Date) {
      dateDayJs = $dayjs(date);
    } else if (date instanceof $dayjs || (typeof date === 'object' && date?.$isDayjsObject)) {
      dateDayJs = date;
    } else {
      throw new Error('Invalid date format');
    }

    if (!dateDayJs.isValid()) {
      throw new Error('Invalid date');
    }
    return dateDayJs;
  }

  /**
   * Convert weekDay into day
   * @param weekDay
   * @param format
   * @returns {*}
   */

  function convertWeekDayIntoDay(weekDay, format = null) {
    const day = $dayjs().day(weekDay).locale(dayjsLocale.value);
    return format ? day.format(format) : day;
  }

  /**
   * Local format date
   * @param date
   * @param hideTime
   * @param showSeconds
   * @returns {*}
   */
  function localFormatDate(date, hideTime = false, showSeconds = true) {
    const dateDayJs = parseDate(date);
    let format = 'L LTS';
    if (hideTime) {
      format = 'L';
    } else if (!showSeconds) {
      format = 'L LT';
    }

    return dateDayJs.local().locale(dayjsLocale.value).format(format);
  }

  /**
   * Local format time
   * @param date
   * @param showSeconds
   * @returns {*}
   */
  function localFormatTime(date, showSeconds = false) {
    const dateTimeDayJs = parseDate(date);
    let format = 'LTS';
    if (!showSeconds) {
      format = 'LT';
    }
    return dateTimeDayJs.local().locale(dayjsLocale.value).format(format);
  }

  /**
   * Convert date to format YY MM DD
   * @param date
   * @returns {*}
   */
  function convertDateToYYMMDD(date) {
    return $dayjs.utc(date).toISOString().substr(0, 10);
  }

  /**
   * Convert date to ISO8601 string keeping the timezone
   * Example: 2021-06-01T00:00:00Z
   * Example: 2021-06-01T00:00:00+02:00
   */
  function toISO8601String(date) {
    return parseDate(date).format('YYYY-MM-DDTHH:mm:ssZ');
  }

  /**
   * Return timezone name with its GMT offset, e.g. "Europe/Paris (GMT+02:00)"
   * @param {string} timeZone - IANA timezone identifier
   * @returns {string}
   */
  function formatTimeZoneWithGMT(timeZone) {
    try {
      // Create Day.js object with the requested timezone; default to now
      const tzDate = $dayjs().tz(timeZone);

      // Offset in minutes. Positive offset means timezone is ahead of UTC.
      const offsetMinutes = tzDate.utcOffset();

      const sign = offsetMinutes >= 0 ? '+' : '-';
      const absMinutes = Math.abs(offsetMinutes);
      const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
      const minutes = String(absMinutes % 60).padStart(2, '0');

      return `${timeZone} (GMT${sign}${hours}:${minutes})`;
    } catch (e) {
      // Fallback: return timezone as is if parsing fails
      return timeZone;
    }
  }

  /**
  * Format duration in seconds to Hh Mm (minutes omitted if zero)
  * @param {number} seconds
  * @returns {string}
  */
  function formatHhMm(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return '0h';
    }

    const totalMinutes = Math.floor(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (minutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h${minutes}m`;
  }
  /**
 * Format duration in seconds to hours and minutes using Day.js
 * @param {number} seconds - Duration in seconds
 * @param {boolean} [forceHours=false] - Force output to hours
 * @returns {string} - Formatted duration (e.g., "11h06" or "45s")
 */
  function formatDuration(seconds, forceHours = false) {
    if (forceHours) {
      if (!Number.isFinite(seconds) || seconds <= 0 || seconds < 3500) {
        return '0h';
      }

      const hours = Math.round(seconds / 3600);
      return `${hours}h`;
    }

    if (seconds < 120) {
      return `${seconds}s`;
    }
    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return remainingSeconds > 0 ? `${minutes}m${remainingSeconds}s` : `${minutes}m`;
    }
    if (seconds < 3600 * 24) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h${minutes}m` : `${hours}h`;
    }
    const hours = Math.round(seconds / 3600);
    return `${hours}h`;
  }

  return {
    $dayjs,
    dayjsLocale,
    parseDate,
    convertDateToYYMMDD,
    convertWeekDayIntoDay,
    localFormatDate,
    localFormatTime,
    formatDuration,
    formatHhMm,
    toISO8601String,
    formatTimeZoneWithGMT,
    ATOM_FORMAT,
  };
}
